import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Wallet, Plus, ArrowUpLeft, ArrowDownRight,
  CreditCard, Building2, Smartphone
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const PassengerWallet: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('bankak');

  const balance = (currentUser as any)?.walletBalance || 0;

  const transactions = [
    { id: 1, type: 'deposit', amount: 5000, method: 'بنكك', date: '2024-01-15', status: 'completed' },
    { id: 2, type: 'payment', amount: 1200, method: 'رحلة #1234', date: '2024-01-14', status: 'completed' },
    { id: 3, type: 'deposit', amount: 3000, method: 'فوري', date: '2024-01-10', status: 'completed' },
    { id: 4, type: 'payment', amount: 850, method: 'رحلة #1230', date: '2024-01-08', status: 'completed' },
  ];

  const depositMethods = [
    { id: 'bankak', name: 'بنكك', icon: Building2 },
    { id: 'fawry', name: 'فوري', icon: CreditCard },
    { id: 'sadad', name: 'ساداد', icon: Smartphone },
  ];

  const handleDeposit = () => {
    // In production, integrate with payment gateway
    alert(`سيتم شحن ${depositAmount} ج.س عبر ${selectedMethod}`);
    setShowDepositModal(false);
    setDepositAmount('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/passenger/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">المحفظة</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Balance Card */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Wallet className="w-6 h-6" />
            </div>
            <span className="text-white/70">رصيدك الحالي</span>
          </div>
          <p className="text-4xl font-bold mb-2">{balance.toLocaleString()} ج.س</p>
          <p className="text-white/70 text-sm">يمكنك استخدام الرصيد لدفع رحلاتك</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => setShowDepositModal(true)}
            className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6 text-green-600" />
            </div>
            <span className="font-medium">شحن الرصيد</span>
          </button>
          <button
            onClick={() => navigate('/passenger/rides')}
            className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <ArrowUpLeft className="w-6 h-6 text-primary-600" />
            </div>
            <span className="font-medium">سجل الرحلات</span>
          </button>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b">
            <h2 className="font-bold">سجل المعاملات</h2>
          </div>
          <div className="divide-y">
            {transactions.map((txn) => (
              <div key={txn.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    txn.type === 'deposit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                  }`}>
                    {txn.type === 'deposit' ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpLeft className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium">
                      {txn.type === 'deposit' ? 'شحن رصيد' : 'دفع رحلة'}
                    </p>
                    <p className="text-gray-500 text-sm">{txn.method}</p>
                  </div>
                </div>
                <div className="text-left">
                  <p className={`font-bold ${txn.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                    {txn.type === 'deposit' ? '+' : '-'}{txn.amount} ج.س
                  </p>
                  <p className="text-gray-400 text-xs">{txn.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-6">شحن الرصيد</h2>

            {/* Amount */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">المبلغ</label>
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder="أدخل المبلغ"
                className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-primary-500"
              />
            </div>

            {/* Payment Method */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">طريقة الدفع</label>
              <div className="space-y-2">
                {depositMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-colors ${
                      selectedMethod === method.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-100'
                    }`}
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                      <method.icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <span className="font-medium">{method.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDepositModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeposit}
                disabled={!depositAmount}
                className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                تأكيد الشحن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerWallet;
